import { type Capabilities } from '@canvas/capability';

type UpdatesFragment = { canSend?: boolean; canConsume?: boolean };
type AwarenessFragment = {
  selections?: UpdatesFragment;
  telepointer?: UpdatesFragment;
  transient?: UpdatesFragment;
};

export type CapabilityOverride = {
  updates?: UpdatesFragment;
  awareness?: AwarenessFragment;
};

function mergeUpdates(
  base: UpdatesFragment | undefined,
  override: UpdatesFragment | undefined,
): UpdatesFragment | undefined {
  if (!override) {
    return base;
  }

  return { ...base, ...override };
}

function mergeAwareness(
  base: AwarenessFragment | undefined,
  override: AwarenessFragment | undefined,
): AwarenessFragment | undefined {
  if (!override) {
    return base;
  }

  return {
    ...base,
    ...(override.selections && {
      selections: { ...base?.selections, ...override.selections },
    }),
    ...(override.telepointer && {
      telepointer: { ...base?.telepointer, ...override.telepointer },
    }),
    ...(override.transient && {
      transient: { ...base?.transient, ...override.transient },
    }),
  };
}

export function mergeOverrides(...overrides: CapabilityOverride[]): CapabilityOverride {
  let result: CapabilityOverride = {};
  for (const o of overrides) {
    const updates = mergeUpdates(result.updates, o.updates);
    const awareness = mergeAwareness(result.awareness, o.awareness);
    result = {
      ...(updates && { updates }),
      ...(awareness && { awareness }),
    };
  }

  return result;
}

export function mergeCapabilityOverride(
  base: Capabilities | undefined,
  override: CapabilityOverride,
): Capabilities | undefined {
  if (!base) {
    return base;
  }

  const result = { ...base } as Capabilities;
  if (override.updates && base.updates) {
    result.updates = mergeUpdates(base.updates, override.updates) as typeof base.updates;
  }

  if (override.awareness && base.awareness) {
    result.awareness = mergeAwareness(
      base.awareness,
      override.awareness,
    ) as typeof base.awareness;
  }

  return result;
}

type OnChange = VoidFunction;

export class CapabilityOverridesApi {
  #overrides: Map<string, CapabilityOverride>;
  #onChange: OnChange;

  constructor(overrides: Map<string, CapabilityOverride>, onChange: OnChange) {
    this.#overrides = overrides;
    this.#onChange = onChange;
  }

  add(namespace: string, override: CapabilityOverride): void {
    this.#overrides.set(namespace, override);
    this.#onChange();
  }

  remove(namespace: string): void {
    this.#overrides.delete(namespace);
    this.#onChange();
  }
}
